import React from 'react';
import { Modal } from 'react-bootstrap';
import { CurrencyInput, Select, SelectActions } from 'components';
import { ServicesService } from 'services';
import { Globals } from 'utils';
import { AddIcon, CloseIcon } from 'assets/icons';
import ModalCreateSpecialty from './modal-create-specialty';

type IonChangeEvent = React.ChangeEvent<HTMLInputElement>;

type Props = {
  service?: any
  onClose: (reload?: boolean) => void
}

interface State {
  modal: number,
  selectedItem: any,
  form: {
    service_id?: number | string,
    specialty_id?: number | string,
    type_id?: number | string,
    price: string,
    doctor_price: string,
  }
  filter: {
    search: string,
  }
  specialties: any[]
  types: any[]
}

const initialForm = {
  service_id: '',
  specialty_id: '',
  type_id: '',
  price: '',
  doctor_price: '',
};

const initialFilter = {
  search: '',
};

const MODAL = {
  NONE: 0,
  CREATE: 1,
  EDIT: 2,
  DELETE: 3,
};

class ModalCreateService extends React.Component<Props, State> {

  state: State = {
    modal: MODAL.NONE,
    selectedItem: null,
    form: { ...initialForm },
    filter: {
      search: '',
    },
    specialties: [],
    types: [],
  }

  debounce?: NodeJS.Timeout;

  componentDidMount(): void {
    this.getSelects();
    if (this.props.service?.id) this.fillForm();
  }

  fillForm = () => {
    this.setState({
      form: {
        service_id: this.props.service?.id,
        specialty_id: this.props.service?.specialty_id,
        type_id: this.props.service?.type_id,
        price: this.props.service?.price,
        doctor_price: this.props.service?.doctor_price,
      },
    });
  }

  getSelects = async () => {
    const toSelect = (data: any) => data?.map((x: any) => ({ value: x.id, label: x.name })) ?? [];

    try {
      const res: any = await ServicesService.getSelects({ withoutLoading: true, ...this.state.filter });
      this.setState({
        specialties: toSelect(res?.specialties),
        types: toSelect(res?.types),
      });

    } catch (error) {
      Globals.showError('Ocurrió un error inesperado');
    }
  }

  changeFilter = async (value: string | number | boolean, target: keyof typeof initialFilter) => {
    const filter = Globals.clone(this.state.filter);
    filter[target] = value;
    await this.setState({ filter });

    if (this.debounce) clearTimeout(this.debounce);
    this.debounce = setTimeout(async () => this.getSelects(), 800);
  }

  change = async (value: string | number | boolean, target: keyof typeof initialForm) => {
    const form = Globals.clone(this.state.form);

    if (target === 'price' || target === 'doctor_price') {
      if (!Globals.validateDouble(value as string) && value !== '') {
        value = form[target];
      }
    }

    form[target] = value;
    await this.setState({ form });
  }

  setModal = (modal: number, item?: any) => {
    this.setState({ modal, selectedItem: item ?? null });
  }

  validateForm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const { form } = this.state;

    if (!form.specialty_id)
      return Globals.showError('Debe seleccionar una especialidad');

    if (!form.type_id)
      return Globals.showError('Debe seleccionar un tipo de consulta');

    if (!form.price || isNaN(+form.price))
      return Globals.showError('Debe ingresar el precio');

    if (!form.doctor_price || isNaN(+form.doctor_price))
      return Globals.showError('Debe ingresar el pago al médico');

    if (+form.doctor_price > +form.price)
      return Globals.showError('El pago al médico no puede ser mayor al precio de la consulta');

    this.submit();
  }

  submit = async () => {
    try {
      if (this.state.form.service_id) {
        await ServicesService.update(this.state.form);
        Globals.showSuccess('Servicio actualizado');

      } else {
        await ServicesService.create(this.state.form);
        Globals.showSuccess('Servicio creado');
      }
      this.setState({ form: { ...initialForm } });
      this.props.onClose(true);

    } catch (error) {
      const { response } = error as any;
      Globals.showError(response?.data?.error || 'Ocurrió un error inesperado');
    }
  }

  render() {

    const { form, modal } = this.state;
    const isEditing = !!form.service_id;

    return (
      <form id="services-create" onSubmit={this.validateForm}>
        <Modal
          id="services-specialty-create"
          show={modal === MODAL.CREATE}
          onHide={() => this.setModal(MODAL.NONE)}
          size="sm"
        >
          <ModalCreateSpecialty
            specialty={this.state.selectedItem}
            onClose={(reload) => {
              this.setModal(MODAL.NONE);
              if (reload) this.getSelects();
            }}
          />
        </Modal>

        <div>
          <SelectActions
            label="Especialidad"
            placeholder="Seleccione"
            isSearchable
            onInputChange={v => this.changeFilter(v, 'search')}
            actionOptions={[
              {
                onClick: () => this.setModal(MODAL.CREATE),
                content: (
                  <div className="btn-icon">
                    <img src={AddIcon} alt="Agregar" />
                    <span>Crear nueva especialidad</span>
                  </div>
                ),
              }
            ]}
            options={this.state.specialties}
            value={form.specialty_id}
            onChange={v => this.change(v, 'specialty_id')}
          />
        </div>
        <Select
          label="Tipo de consulta"
          name="type_id"
          value={form.type_id}
          onChange={(e: IonChangeEvent) => this.change(e.target.value, 'type_id')}
          options={this.state.types}
        />
        <CurrencyInput
          label="Precio"
          placeholder={isEditing ? this.props.service?.price : '0,00'}
          value={form.price}
          onValueChange={v => this.change(String(v), 'price')}
        />
        <CurrencyInput
          label="Pago al médico"
          placeholder={isEditing ? this.props.service?.doctor_price : '0,00'}
          value={form.doctor_price}
          onValueChange={v => this.change(String(v), 'doctor_price')}
        />
        <div className="actions">
          <button type="button" className="close-btn" onClick={() => this.props.onClose()} title="Cerrar">
            <img src={CloseIcon} alt="Cerrar" />
          </button>

          <button type="submit" className="submit">
            <span>Aceptar</span>
          </button>

          <button type="button" className="cancel" onClick={() => this.props.onClose()} title="Cancelar">
            <span>Cancelar</span>
          </button>
        </div>
      </form>
    )
  }
}

export default ModalCreateService;